import React, {
  useCallback,
  MouseEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Stack from '@mui/material/Stack';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import {
  phoneValidation,
  stringValidation,
  isEmptyString,
  formatDate,
  formatDateOnly,
  parseErrorMessage,
} from 'utils/helper';
import dayjs from 'dayjs';
import Label from 'components/common/label';
import dynamic from 'next/dynamic';
import MetaTag from 'components/meta';
import styles from 'components/medicalHistory/page.module.scss';
import Button from 'components/common/button';
import InputText from 'components/common/InputText';
import UploaderElement from 'components/common/uploaderElement';
import {
  LOCAL_STORAGE_MH_KEY,
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import {
  NullableAppointmentDetails,
} from 'types/types';
import cloneDeep from 'lodash/cloneDeep';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import clsx from 'clsx';
import Autocomplete from '@mui/material/Autocomplete';
import {
  USStateType,
  US_STATES,
} from 'lib/usStateOptions';
import TextField from '@mui/material/TextField';
import {
  patientSex,
  MultimediaType,
} from 'utils/constants';
import {
  AddressDTO,
  InsuranceAttachmentDTO,
  InsuranceDTO,
  InsuranceSlot,
  MultiMediaResponse,
} from 'artifacts/dtos';
import { NullablePatientDTO } from 'types/patients';
import InsuranceLookup from 'components/medicalHistory/insuranceLookup';
import Wrapper from 'components/common/Wrapper';
import ZipCodeTextField from 'components/common/ZipCodeTextField';
import PhoneInput from 'components/common/PhoneInput';
import { getAllowedFileTypesByCategory } from 'utils/files';
import StatusTag from 'components/common/statusTag';
import isNil from 'lodash/isNil';
import IconButton from '@mui/material/IconButton';
import ToolTip from 'components/common/tooltip';
import isNumber from 'lodash/isNumber';
import AddressSuggestion, { Place } from 'components/common/AddressSuggestion';
import { formatPlace } from 'utils/misc';
import { useGetInsuranceSettings, useRestrictedInsuranceSlotConflict } from 'hooks/useHook';
import Spinner from 'components/spinner/Spinner';
import Dialog from 'components/common/Dialog';
import {
  useGetInsuranceAttachments,
  useGetUnassociatedInsuranceCards,
  useAddPatientInsurance,
  useGetPatientByIdWithPharmacy,
  useUploadAndAssociateInsuranceCard,
} from 'hooks/useApi';
import MediaPreview from 'components/common/MediaPreview';
import axios from 'axios';
import { actionTypeOfMedicalHistory } from 'reducer/actionType';
import { patientAction } from 'reduxStateSlice/patient';

const MuiDatePicker = dynamic(() => import('../common/datePicker'), { ssr: false });

type InsuranceActionProps = {
  hasInsuranceId: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>
  text?: string;
  className?: string;
  tooltip?: string;
  hide?: boolean;
}

function InsuranceAction({
  hasInsuranceId,
  onClick,
  text,
  className,
  tooltip,
  hide,
}: InsuranceActionProps) {
  const hasText = !isEmptyString(text);
  const title = hasInsuranceId
    ? 'This insurance cannot be removed at this time. For assistance, please reach out to your doctor\'s office.'
    : 'Remove';
  return (
    <ToolTip
      title={isEmptyString(tooltip) ? title : tooltip}
      hide={hide}
    >
      <span>
        <IconButton
          disabled={hasInsuranceId}
          onClick={onClick}
          className={clsx(className)}
        >
          {!hasText ? (
            <ClearIcon
              fontSize="small"
              color="action"
            />
          ) : (<span className="text-sm text-populate-green">{text}</span>)}
        </IconButton>
      </span>
    </ToolTip>
  );
}

type InsuranceAttachmentCardProps = {
  insuranceId: number;
  patientId?: number;
};

function InsuranceAttachmentCards({
  insuranceId,
  patientId,
}: InsuranceAttachmentCardProps) {
  const { data: attachments = [], isLoading } = useGetInsuranceAttachments(insuranceId);
  const mediaList = attachments
    .map((attachment: InsuranceAttachmentDTO) => attachment.media)
    .filter((media): media is MultiMediaResponse => !isNil(media));

  return (
    <div className="flex flex-col gap-3">
      {isLoading && (
        <div className="flex items-center gap-2 text-xs">
          <Spinner />
          Loading insurance cards...
        </div>
      )}
      {!isLoading && mediaList.length === 0 && (
        <p className="text-xs text-gray-500">No insurance cards found.</p>
      )}
      {!isLoading && mediaList.map((media) => (
        <MediaPreview
          key={media.mediaId ?? media.url?.value}
          thumbnail
          media={media}
          patientId={patientId}
          hasDelete={false}
          className="!my-1 !h-28 !min-h-28 rounded border border-slate-200"
        />
      ))}
    </div>
  );
}

type UnassociatedInsuranceCardsProps = {
  patientId: number;
};

function UnassociatedInsuranceCards({ patientId }: UnassociatedInsuranceCardsProps) {
  const { data, isLoading, refetch } = useGetUnassociatedInsuranceCards(patientId);
  const mediaList = data?.list ?? [];

  return (
    <div
      className="flex flex-col justify-start rounded-md bg-white h-full flex-[0_0_18rem] pt-4"
    >
      <div className="mb-4 px-2">
        <h3 className="text-base font-semibold">Unlinked Insurance Cards</h3>
      </div>
      <div className="flex flex-col space-y-2.5 overflow-y-auto px-2">
        {isLoading && (
          <div className="flex flex-auto justify-start items-center text-xs">
            <Spinner />
            Loading unlinked insurance cards...
          </div>
        )}
        {mediaList.map((media) => (
          <MediaPreview
            key={media.mediaId ?? media.url?.value}
            thumbnail
            media={media}
            patientId={patientId}
            refetch={async () => { await refetch(); }}
            hasDelete={false}
            className="!my-2 !h-28 !min-h-28"
          />
        ))}
        {!isLoading && mediaList.length === 0 && (
          <p className="text-xs">
            No Insurance cards found
          </p>
        )}
      </div>
    </div>
  );
}

const enum InputTextName {
  insuranceFrontSide = 'insuranceFrontSide',
  insuranceBackSide = 'insuranceBackSide',
  insuranceOwnerRelationshipType = 'insuranceOwnerRelationshipType',
  firstName = 'firstName',
  middleName = 'middleName',
  lastName = 'lastName',
  phoneNumber = 'phoneNumber',
  employerName = 'employerName',
  gender = 'gender',
  insuranceName = 'insuranceName',
  memberNumber = 'memberNumber',
  dob = 'dob',
  apartment = 'apartment',
  unit = 'unit',
  street = 'street',
  city = 'city',
  insuranceState = 'insuranceState',
  zip = 'zip',
  contactState = 'contactState',
}

export type InsuranceFormType = {
  insuranceId?: number;
  insuranceFrontSide?: string;
  insuranceBackSide?: string;
  insuranceOwnerRelationshipType?: string;
  firstName?: string;
  middleName?: string;
  employerName?: string;
  insuranceName?: string;
  memberNumber?: string;
  lastName?: string;
  phoneNumber?: string;
  gender?: string;
  dob?: string;
  place?: Place;
  apartment?: string;
  unit?: string;
  street?: string;
  city?: string;
  contactState?: string;
  zip?: string;
  insuranceSlot?: InsuranceSlot;
  chPayerListingId?: number;
  insuranceCardCPID?: string;
  copay?: number | null;
  inactive?: boolean;
  lastInactiveDate?: string;
  payerEntryId?: number;
  payerId?: string;
};

export function serializeInsuranceForm(
  {
    insuranceOwnerRelationshipType,
    employerName,
    insuranceName,
    insuranceSlot,
    memberNumber,
    firstName,
    middleName,
    lastName,
    gender,
    dob,
    phoneNumber,
    chPayerListingId,
    insuranceFrontSide,
    insuranceBackSide,
    ...insuranceValue
  }: InsuranceFormType,
  patient: NullablePatientDTO,
  personalDob: string,
  patientAddress: AddressDTO,
): InsuranceDTO {
  const isSubscriberPatient = insuranceOwnerRelationshipType === 'Self';
  const isEmployerInsurance = insuranceOwnerRelationshipType === 'Employee';
  const isSpecialOwner = isSubscriberPatient || isEmployerInsurance;
  const insuranceOwnerAddress: AddressDTO = isSpecialOwner
    ? patientAddress
    : {
      unit: isEmptyString(insuranceValue.unit) ? null : insuranceValue.unit,
      street: isEmptyString(insuranceValue.street) ? null : insuranceValue.street,
      city: isEmptyString(insuranceValue.city) ? null : insuranceValue.city,
      state: isEmptyString(insuranceValue.contactState) ? null : insuranceValue.contactState,
      zipCode: isEmptyString(insuranceValue.zip) ? null : insuranceValue.zip,
      country: 'USA',
    };
  const insuranceOwnerPhoneNumber = !isEmptyString(phoneNumber) && !isSpecialOwner
    ? `+${phoneNumber}`
    : patient?.phoneNumber?.phoneNumberWithAreaCode ?? null;

  return {
    insuranceOwnerRelationshipType: insuranceOwnerRelationshipType ?? null,
    insuranceOwnerFirstName: isSpecialOwner ? patient?.firstName ?? null : firstName,
    insuranceOwnerMiddleName: isSpecialOwner ? patient?.middleName ?? null : middleName,
    insuranceOwnerLastName: isSpecialOwner ? patient?.lastName ?? null : lastName,
    insuranceOwnerDOB: isSpecialOwner ? formatDateOnly(personalDob) : formatDateOnly(dob ?? ''),
    insuranceOwnerGenderDescription: isSpecialOwner
      ? patient?.genderDescription ?? null : gender ?? null,
    insuranceOwnerPhoneNumber,
    demographics: isSpecialOwner
      ? {
        dateOfBirth: patient?.dateOfBirth ?? '',
        firstName: patient?.firstName ?? '',
        middleName: patient?.middleName ?? '',
        lastName: patient?.lastName ?? '',
        genderDescription: patient?.genderDescription ?? '',
      }
      : undefined,
    employerName: isEmptyString(employerName) ? null : employerName,
    insuranceName: insuranceName ?? null,
    insuranceSlot,
    insuranceOwnerAddress,
    groupName: null,
    groupNumber: null,
    memberNumber: memberNumber ?? null,
    insuranceType: null,
    effectiveFrom: formatDateOnly(dayjs()),
    coverageType: null,
    coverageLevel: null,
    chPayerListingId: isEmptyString(chPayerListingId) ? null : chPayerListingId,
    insuranceCardCPID: null,
    ...insuranceValue,
  } as InsuranceDTO;
}

const initialInsuranceForm: InsuranceFormType = {
  insuranceFrontSide: '',
  insuranceBackSide: '',
  insuranceOwnerRelationshipType: '',
  insuranceName: '',
  memberNumber: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  gender: '',
  dob: '',
  apartment: '',
  employerName: '',
  unit: '',
  street: '',
  city: '',
  contactState: '',
  zip: '',
};

const checkFieldsKeyByIndex = [
  'insuranceFrontSide',
  'insuranceBackSide',
  'insuranceOwnerRelationshipType',
  'insuranceName',
  'memberNumber',
  'firstName',
  'middleName',
  'lastName',
  'phoneNumber',
  'gender',
  'dob',
  'employerName',
  'place',
  'apartment',
  'unit',
  'street',
  'city',
  'contactState',
  'zip',
  'chPayerListingId',
] as const;

type InsuranceFieldType = typeof checkFieldsKeyByIndex[number];

const emptyStringFields = [
  'firstName',
  'lastName',
  'dob',
  'gender',
] as const;

const defaultRequiredFields = [
  'insuranceOwnerRelationshipType',
  'insuranceName',
  'memberNumber',
  'insuranceSlot',
] as const;

const validationFocusOrder: (keyof InsuranceFormType)[] = [
  'insuranceOwnerRelationshipType',
  'insuranceName',
  'memberNumber',
  'employerName',
  'firstName',
  'lastName',
  'gender',
  'dob',
  'phoneNumber',
  'insuranceSlot',
];

const addressFields = new Set([
  'zip',
  'phoneNumber',
  'dob',
  'street',
  'unit',
]);

const insuranceCards = new Set([
  'insuranceFrontSide',
  'insuranceBackSide',
]);

const hasInsuranceCardUploads = (insurance: InsuranceFormType) => (
  !isEmptyString(insurance.insuranceFrontSide)
  || !isEmptyString(insurance.insuranceBackSide)
);

type InsuranceCardField = 'insuranceFrontSide' | 'insuranceBackSide';

function removeInsuranceCardUpload(
  insurance: InsuranceFormType,
  field: InsuranceCardField,
): InsuranceFormType {
  const updatedInsurance = { ...insurance };
  delete updatedInsurance[field];
  return updatedInsurance;
}

const isSameNewInsurance = (
  savedInsurance: InsuranceDTO,
  formInsurance: InsuranceFormType,
) => savedInsurance.insuranceName === formInsurance.insuranceName
  && savedInsurance.memberNumber === formInsurance.memberNumber
  && savedInsurance.insuranceOwnerRelationshipType
    === formInsurance.insuranceOwnerRelationshipType
  && savedInsurance.insuranceSlot === formInsurance.insuranceSlot;

const initialErrors = checkFieldsKeyByIndex.reduce((accumulator, value) => (
  { ...accumulator, [value]: false }
), {});

const titleText = 'Insurance or Out of Pocket';

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

const INSURANCE_SLOT_ORDER: Partial<Record<InsuranceSlot, number>> = {
  [InsuranceSlot.Primary]: 0,
  [InsuranceSlot.PrimaryDME]: 1,
  [InsuranceSlot.Secondary]: 2,
  [InsuranceSlot.Tertiary]: 3,
  [InsuranceSlot.Other]: 4,
};

function sortInsurances(a: InsuranceFormType, b: InsuranceFormType): number {
  // Inactive entries always sink to the bottom
  if (a.inactive !== b.inactive) {
    return a.inactive ? 1 : -1;
  }
  // Among same activity status, order by billing slot
  const aOrder = !isNil(a.insuranceSlot) ? (INSURANCE_SLOT_ORDER[a.insuranceSlot] ?? 5) : 5;
  const bOrder = !isNil(b.insuranceSlot) ? (INSURANCE_SLOT_ORDER[b.insuranceSlot] ?? 5) : 5;
  return aOrder - bOrder;
}

function normalizeInsurance({
  insuranceOwnerRelationshipType,
  insuranceOwnerFirstName: firstName,
  insuranceOwnerMiddleName: middleName,
  insuranceOwnerLastName: lastName,
  insuranceOwnerAddress: address,
  insuranceOwnerDOB: dob,
  insuranceOwnerPhoneNumber: phoneNumber,
  insuranceOwnerGenderDescription: gender,
  ...insurance
}: InsuranceDTO): InsuranceFormType {
  return {
    ...(insurance ?? {}),
    ...(address ?? {}),
    contactState: address?.state,
    zip: address?.zipCode,
    insuranceOwnerRelationshipType,
    firstName,
    middleName,
    lastName,
    dob,
    gender,
    phoneNumber: phoneNumber?.replace(/[^0-9]/, ''),
    ...insurance,
    insuranceSlot: insurance.insuranceSlot,
  } as InsuranceFormType;
}

type PersistedInsuranceState = {
  data: InsuranceFormType[];
  isCashPay?: boolean;
};

function getPersistedInsuranceState(
  medicalHistoryState: StateTypeLocalStorage[],
  actionPage: string,
): PersistedInsuranceState {
  const value = medicalHistoryState.find((page) => page.name === actionPage)?.value;
  if (isNil(value) || typeof value !== 'object' || Array.isArray(value)) {
    return { data: [] };
  }

  const persistedState = value as Partial<PersistedInsuranceState>;
  return {
    data: Array.isArray(persistedState.data) ? persistedState.data : [],
    isCashPay: persistedState.isCashPay,
  };
}

function updatePersistedInsuranceState(
  medicalHistoryState: StateTypeLocalStorage[],
  actionPage: string,
  insurances: InsuranceFormType[],
  isCashPay: boolean,
): StateTypeLocalStorage[] {
  return medicalHistoryState.map((page) => (
    page.name === actionPage
      ? {
        ...page,
        title: 'Insurance ID',
        value: { data: insurances, isCashPay },
      }
      : page
  ));
}

function mergeRefreshedAndPersistedInsurances(
  refreshedInsurances: InsuranceDTO[],
  persistedInsurances: InsuranceFormType[],
): InsuranceFormType[] {
  const persistedById = new Map(
    persistedInsurances
      .filter((insurance) => !isNil(insurance.insuranceId))
      .map((insurance) => [insurance.insuranceId, insurance]),
  );
  const normalizedInsurances = refreshedInsurances
    .map((insurance) => {
      const persistedInsurance = persistedById.get(insurance.insuranceId);
      const normalizedInsurance = normalizeInsurance(insurance);
      if (!isEmptyString(persistedInsurance?.insuranceFrontSide)) {
        normalizedInsurance.insuranceFrontSide = persistedInsurance?.insuranceFrontSide;
      }
      if (!isEmptyString(persistedInsurance?.insuranceBackSide)) {
        normalizedInsurance.insuranceBackSide = persistedInsurance?.insuranceBackSide;
      }
      return normalizedInsurance;
    })
    .sort(sortInsurances);
  const unsavedInsurances = persistedInsurances.filter(
    (insurance) => isNil(insurance.insuranceId),
  );
  const insuranceList = [...normalizedInsurances, ...unsavedInsurances];

  return insuranceList.length > 0
    ? insuranceList
    : [{ ...initialInsuranceForm }];
}

function resolveCashPay(
  persistedCashPay: boolean | undefined,
  appointmentCashPay: boolean | undefined,
): boolean {
  return persistedCashPay ?? appointmentCashPay ?? false;
}

function getInsuranceRefreshErrorMessage(error: unknown): string {
  return axios.isAxiosError(error)
    ? parseErrorMessage(error)
    : 'Unable to refresh insurance information.';
}

function InsuranceSavingOverlay() {
  return (
    <div
      className={styles.insuranceSavingOverlay}
      role="status"
      aria-live="polite"
      aria-label="Saving insurance and insurance cards"
    >
      <div className={styles.insuranceSavingContent}>
        <Spinner name="FadeLoader" />
        <p>Saving insurance...</p>
      </div>
    </div>
  );
}

function InsuranceIdScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const {
    relationshipTypes,
    relationshipTypesByName,
    isLoading,
    errorMessage,
    insuranceSlotOptions,
    insuranceSlotValues,
    getInsuranceSlotLabel,
  } = useGetInsuranceSettings();

  const restrictedSlotSet = useMemo(
    () => new Set(
      insuranceSlotOptions
        .filter((opt) => opt.isRestrictedToUniqueActiveInsurance)
        .map((opt) => opt.slot),
    ),
    [insuranceSlotOptions],
  );

  const [isCashPay, setIsCashPay] = useState(false);
  const [isUploadingCards, setIsUploadingCards] = useState(false);
  const [isRefreshingInsurance, setIsRefreshingInsurance] = useState(true);
  const [refreshErrorMessage, setRefreshErrorMessage] = useState('');
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const reduxDispatch = useDispatch();
  const { mutateAsync: addPatientInsurance } = useAddPatientInsurance();
  const { mutateAsync: getPatientByIdWithPharmacy } = useGetPatientByIdWithPharmacy();
  const { mutateAsync: uploadAndAssociateInsuranceCard } = useUploadAndAssociateInsuranceCard();
  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;
  const appointmentData = useSelector(
    (state: RootState) => state.appointment,
  ) as NullableAppointmentDetails;

  const [listOfInsurance, setListOfInsurance] = useState<InsuranceFormType[]>([]);
  const { insurances: patientInsurances = [] } = patient ?? {};
  const hasInsurance = patientInsurances.length > 0;

  const allFormInsurances = useMemo(
    () => listOfInsurance.map(({ insuranceId, inactive, insuranceSlot }, idx) => ({
      // New items have no insuranceId; assign a unique negative pseudo-ID so the
      // hook's self-exclusion logic (insuranceId !== currentInsuranceId) works
      // correctly between multiple new entries in the same session.
      insuranceId: insuranceId ?? -(idx + 1),
      inactive,
      insuranceSlot,
    })) as InsuranceDTO[],
    [listOfInsurance],
  );

  const checkSlotConflict = useRestrictedInsuranceSlotConflict(
    allFormInsurances,
    restrictedSlotSet,
  );

  // error state
  const [errors, _setErrors] = useState<Record<string, boolean>[]>([]);

  const setErrors = (index: number, errors?: Record<string, boolean>) => {
    if (!errors) return;
    _setErrors((currentErrors) => currentErrors.map((item, i) => (
      index === i ? { ...item, ...errors } : item
    )));
  };

  const [state, dispatch] = usePersistMHReducer();
  const initialMedicalHistoryState = useRef(state);

  const persistInsuranceList = useCallback((
    items: InsuranceFormType[],
    cashPay: boolean,
  ) => {
    const nextMedicalHistoryState = updatePersistedInsuranceState(
      initialMedicalHistoryState.current,
      actionPage,
      items,
      cashPay,
    );

    initialMedicalHistoryState.current = nextMedicalHistoryState;
    window.localStorage.setItem(
      LOCAL_STORAGE_MH_KEY,
      JSON.stringify(nextMedicalHistoryState),
    );
    setListOfInsurance(items);
    dispatch({
      type: actionPage,
      name: 'Insurance ID',
      value: { data: items, isCashPay: cashPay },
    });
  }, [actionPage, dispatch]);

  useEffect(() => {
    let isActive = true;
    const patientId = patient?.patientId;

    const refreshInsurance = async () => {
      if (isNil(patientId)) {
        setIsRefreshingInsurance(false);
        return;
      }

      try {
        const refreshedPatient = await getPatientByIdWithPharmacy(patientId);
        if (!isActive) return;

        const persistedState = getPersistedInsuranceState(
          initialMedicalHistoryState.current,
          actionPage,
        );
        const nextInsuranceList = mergeRefreshedAndPersistedInsurances(
          refreshedPatient.insurances ?? [],
          persistedState.data,
        );
        const refreshedCashPay = resolveCashPay(
          persistedState.isCashPay,
          appointmentData?.appointment?.isOutOfPocket,
        );

        reduxDispatch(patientAction(refreshedPatient));
        _setErrors(nextInsuranceList.map(() => ({ ...initialErrors })));
        setIsCashPay(refreshedCashPay);
        persistInsuranceList(nextInsuranceList, refreshedCashPay);
      } catch (error: unknown) {
        if (!isActive) return;
        setRefreshErrorMessage(getInsuranceRefreshErrorMessage(error));
      } finally {
        if (isActive) {
          setIsRefreshingInsurance(false);
        }
      }
    };

    refreshInsurance();

    return () => {
      isActive = false;
    };
  }, [
    actionPage,
    appointmentData?.appointment?.isOutOfPocket,
    dispatch,
    getPatientByIdWithPharmacy,
    patient?.patientId,
    persistInsuranceList,
    reduxDispatch,
  ]);

  const focusFirstInvalidField = (validationErrors: Record<string, boolean>[]) => {
    const invalidField = validationErrors.flatMap((item, index) => (
      validationFocusOrder
        .filter((field) => item[field])
        .map((field) => ({ field, index }))
    ))[0];

    if (!invalidField) return;

    window.requestAnimationFrame(() => {
      const insuranceContainer = document.querySelector<HTMLElement>(
        `[data-insurance-index="${invalidField.index}"]`,
      );
      const fieldContainer = insuranceContainer?.querySelector<HTMLElement>(
        `[data-insurance-field="${invalidField.field}"]`,
      );
      const focusTarget = fieldContainer?.matches('input, select, button, textarea')
        ? fieldContainer
        : fieldContainer?.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), button:not([disabled]), textarea:not([disabled]), [role="combobox"]',
        );

      fieldContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      focusTarget?.focus({ preventScroll: true });
    });
  };

  const saveNewInsurances = async (items: InsuranceFormType[]) => {
    const patientId = patient?.patientId;
    if (isNil(patientId) || isNil(patient)) {
      throw new Error('Unable to add insurance because the patient was not found.');
    }

    const newItems = items.filter((item) => isNil(item.insuranceId));
    if (newItems.length === 0) return items;

    const personalInformation = state.find(
      (page: StateTypeLocalStorage) => (
        page.name === actionTypeOfMedicalHistory.PERSONAL_INFORMATION
      ),
    )?.value as Record<string, string> | undefined;
    const contactInformation = state.find(
      (page: StateTypeLocalStorage) => (
        page.name === actionTypeOfMedicalHistory.CONTACT_INFORMATION
      ),
    )?.value as Record<string, string> | undefined;
    const patientAddress: AddressDTO = {
      unit: isEmptyString(contactInformation?.unit) ? null : contactInformation?.unit,
      street: contactInformation?.street ?? null,
      city: contactInformation?.city ?? null,
      state: contactInformation?.contactState ?? null,
      zipCode: contactInformation?.zip ?? null,
      country: 'USA',
    };
    const existingInsuranceIds = new Set(
      patientInsurances.map((insurance) => insurance.insuranceId).filter(Boolean),
    );
    const payloadInsurances = newItems.map((item) => serializeInsuranceForm(
      item,
      patient,
      personalInformation?.dob ?? patient.dateOfBirth ?? '',
      patientAddress,
    ));
    let savedPatient = await addPatientInsurance({
      patientId,
      insurances: payloadInsurances,
    });
    let addedInsurances = savedPatient.insurances?.filter(
      (insurance) => !isNil(insurance.insuranceId)
        && !existingInsuranceIds.has(insurance.insuranceId),
    ) ?? [];

    if (addedInsurances.length < newItems.length) {
      savedPatient = await getPatientByIdWithPharmacy(patientId);
      addedInsurances = savedPatient.insurances?.filter(
        (insurance) => !isNil(insurance.insuranceId)
          && !existingInsuranceIds.has(insurance.insuranceId),
      ) ?? [];
    }

    const usedInsuranceIds = new Set<number>();
    const savedItems = items.map((item) => {
      if (!isNil(item.insuranceId)) return item;

      const savedInsurance = addedInsurances.find((insurance) => {
        const insuranceId = insurance.insuranceId ?? 0;
        return !usedInsuranceIds.has(insuranceId)
          && isSameNewInsurance(insurance, item);
      }) ?? addedInsurances.find((insurance) => {
        const insuranceId = insurance.insuranceId ?? 0;
        return !usedInsuranceIds.has(insuranceId);
      });
      const insuranceId = savedInsurance?.insuranceId;

      if (isNil(insuranceId)) {
        throw new Error('The insurance was added, but its ID could not be retrieved.');
      }

      usedInsuranceIds.add(insuranceId);
      return { ...item, insuranceId };
    });

    reduxDispatch(patientAction(savedPatient));
    persistInsuranceList(savedItems, isCashPay);
    return savedItems;
  };

  const uploadAssociatedInsuranceCards = async (items: InsuranceFormType[]) => {
    const patientId = patient?.patientId;
    if (items.some(hasInsuranceCardUploads) && isNil(patientId)) {
      throw new Error('Unable to upload the insurance card because the patient was not found.');
    }

    const updatedItems = [...items];
    /* eslint-disable no-await-in-loop -- persist each completed upload so retries are idempotent */
    for (let index = 0; index < updatedItems.length; index += 1) {
      let item = { ...updatedItems[index] };
      if (hasInsuranceCardUploads(item) && isNil(item.insuranceId)) {
        throw new Error('Unable to upload the card because the insurance was not added.');
      }

      const uploadCard = async (dataUrl: string, side: 'FrontSide' | 'BackSide') => {
        await uploadAndAssociateInsuranceCard({
          insuranceId: item.insuranceId,
          media: {
            patientId: patientId ?? 0,
            multimediaType: MultimediaType.InsuranceCard,
            dataUrl,
            fileName: `${side}-${item.insuranceId}-${Date.now()}.${dataUrl.split(';')[0].split('/')[1] ?? 'jpeg'}`,
          },
        });
      };

      if (!isEmptyString(item.insuranceFrontSide)) {
        await uploadCard(item.insuranceFrontSide ?? '', 'FrontSide');
        item = removeInsuranceCardUpload(item, 'insuranceFrontSide');
        updatedItems[index] = item;
        persistInsuranceList([...updatedItems], isCashPay);
      }
      if (!isEmptyString(item.insuranceBackSide)) {
        await uploadCard(item.insuranceBackSide ?? '', 'BackSide');
        item = removeInsuranceCardUpload(item, 'insuranceBackSide');
        updatedItems[index] = item;
        persistInsuranceList([...updatedItems], isCashPay);
      }
    }
    /* eslint-enable no-await-in-loop */

    return updatedItems;
  };

  const clickNextOrSubmit = async (nextPage: number, index?: number) => {
    const insurances = isNumber(index)
      ? listOfInsurance.filter((item, i) => i !== index) : listOfInsurance;
    const nextErrors = insurances.map((item: InsuranceFormType) => {
      const itemErrors = { ...initialErrors } as Record<string, boolean>;

      if (Object.values(item).every(isEmptyString)) {
        Object.keys(itemErrors).forEach((field) => {
          itemErrors[field] = true;
        });
      } else {
        defaultRequiredFields.forEach((field) => {
          if (isEmptyString(item[field])) {
            itemErrors[field] = true;
          }
        });

        const { isEmployerInsurance, isSubscriberPatient } = relationshipTypesByName[item?.insuranceOwnerRelationshipType ?? ''] ?? {};

        if (isEmployerInsurance) {
          if (isEmptyString(item.employerName)) {
            itemErrors.employerName = true;
          }
        }

        if (!isEmployerInsurance && !isSubscriberPatient) {
          emptyStringFields.forEach((field) => {
            if (isEmptyString(item[field])) {
              itemErrors[field] = true;
            }
          });
          if (!phoneValidation(item?.phoneNumber ?? '')) {
            itemErrors.phoneNumber = true;
          }
        }
      }
      return itemErrors;
    });
    setListOfInsurance(insurances);
    _setErrors(nextErrors);

    const findFormError = nextErrors.find(
      (item) => Object.values(item).some((v) => v),
    );
    if (findFormError) {
      focusFirstInvalidField(nextErrors);
      return;
    }

    const validInsuranceList = insurances.filter(
      (item) => !isEmptyString(item.insuranceOwnerRelationshipType)
      || !isEmptyString(item.insuranceName) || !isEmptyString(item.memberNumber),
    );

    setIsUploadingCards(true);
    setUploadErrorMessage('');

    try {
      const savedInsuranceList = await saveNewInsurances(validInsuranceList);
      const insuranceListWithUploadedCards = await uploadAssociatedInsuranceCards(
        savedInsuranceList,
      );
      persistInsuranceList(insuranceListWithUploadedCards, isCashPay);

      nextOrSubmitButton(nextPage);
    } catch (error: unknown) {
      let message = 'Unable to upload the insurance card.';
      if (axios.isAxiosError(error)) {
        message = parseErrorMessage(error);
      } else if (error instanceof Error) {
        message = error.message;
      }
      setUploadErrorMessage(message);
    } finally {
      setIsUploadingCards(false);
    }
  };

  const changeInsuranceSlot = (slot: InsuranceSlot | undefined, index: number) => {
    setListOfInsurance((prevList) => [
      ...prevList.slice(0, index),
      { ...prevList[index], insuranceSlot: slot },
      ...prevList.slice(index + 1),
    ]);
    if (slot) {
      setErrors(index, { insuranceSlot: false });
    }
  };

  // Info dialog — shown when selected slot is already occupied (not overridable)
  const [slotConflictDialogOpen, setSlotConflictDialogOpen] = useState(false);
  const [conflictingSlotLabel, setConflictingSlotLabel] = useState('');
  const [conflictingInsuranceName, setConflictingInsuranceName] = useState('');

  const handleSlotChange = (slot: InsuranceSlot | undefined, index: number) => {
    const currentInsuranceId = listOfInsurance[index]?.insuranceId ?? -(index + 1);
    if (checkSlotConflict({
      selectedInsuranceSlot: slot,
      isActiveInsurance: true,
      currentInsuranceId,
    })) {
      const conflicting = listOfInsurance.find((ins, i) => {
        const insId = ins.insuranceId ?? -(i + 1);
        return insId !== currentInsuranceId && !ins.inactive && ins.insuranceSlot === slot;
      });
      setConflictingInsuranceName(conflicting?.insuranceName ?? '');
      setConflictingSlotLabel(slot ? getInsuranceSlotLabel(slot) : '');
      setSlotConflictDialogOpen(true);
      return;
    }
    changeInsuranceSlot(slot, index);
  };

  const handleCloseSlotConflictDialog = () => {
    setSlotConflictDialogOpen(false);
    setConflictingSlotLabel('');
    setConflictingInsuranceName('');
  };

  // Back to state values
  useEffect(() => {
    if (state) {
      const insuranceData = state.find(
        (page: StateTypeLocalStorage) => page.name === actionPage,
      );
      if (Object.keys(insuranceData?.value).length) {
        setListOfInsurance(insuranceData?.value.data);
        _setErrors(insuranceData?.value?.data?.map((_: unknown) => ({ ...initialErrors })));
        setIsCashPay(insuranceData?.value?.isCashPay);
      } else {
        const initialInsurances = hasInsurance
          ? patientInsurances
            .map((insurance) => normalizeInsurance(insurance ?? {}))
            .sort(sortInsurances)
          : [{ ...initialInsuranceForm }];
        const initialInsurancesErrorState = hasInsurance
          ? initialInsurances.map((_) => ({ ...initialErrors }))
          : [{ ...initialErrors }];
        setListOfInsurance(initialInsurances);
        _setErrors(initialInsurancesErrorState);
        const isOutOfPocket = appointmentData?.appointment?.isOutOfPocket;
        if (isOutOfPocket) {
          setIsCashPay(isOutOfPocket);
        }
      }
    }
  }, [state, patientInsurances, isLoading]);

  const removeInsurance = (index: number) => {
    if (index >= 0) {
      setListOfInsurance((currentInsurances) => {
        const cloneInsurance = cloneDeep(currentInsurances);
        cloneInsurance.splice(index, 1);
        return cloneInsurance;
      });
      _setErrors((currentErrors) => {
        const cloneErrors = cloneDeep(currentErrors);
        cloneErrors.splice(index, 1);
        return cloneErrors;
      });
    }
  };

  const onChangeInputValue = (
    index: number,
    fieldName: InsuranceFieldType,
    value: any,
  ) => {
    setListOfInsurance((prevList) => {
      const updatedInsurance = prevList.map((insuranceInfo, i: number) => {
        if (index === i) {
          if (insuranceCards.has(fieldName)) {
            insuranceInfo[fieldName] = value;
            return insuranceInfo;
          }
          if (fieldName === 'insuranceName') {
            insuranceInfo[fieldName] = value?.payerName ?? '';
            insuranceInfo.chPayerListingId = value?.chPayerListingId ?? '';
            insuranceInfo.insuranceCardCPID = value?.payerCPID ?? '';
            insuranceInfo.payerEntryId = value?.payerEntryId;
            insuranceInfo.payerId = value?.payerId;
            setErrors(index, { [fieldName]: false });
            return insuranceInfo;
          }
          if (fieldName === 'memberNumber' && value?.length <= 60) {
            insuranceInfo[fieldName] = value;
            setErrors(index, { [fieldName]: false });
            return insuranceInfo;
          }
          if (addressFields.has(fieldName)) {
            const inputValue = value.trimStart();
            insuranceInfo[fieldName] = inputValue;
            insuranceInfo.place = formatPlace({
              ...insuranceInfo,
              zipCode: insuranceInfo.zip,
              shortZipCode: insuranceInfo.zip,
            } as AddressDTO).place;
            setErrors(index, { [fieldName]: false });
            return insuranceInfo;
          }
          const { isEmployerInsurance, isSubscriberPatient } = relationshipTypesByName[value] ?? {};
          const isSelfInsurance = (isEmployerInsurance || isSubscriberPatient) && fieldName === 'insuranceOwnerRelationshipType';

          if (fieldName) {
            if ((stringValidation(value) && value?.length <= 60) || isSelfInsurance) {
              insuranceInfo[fieldName] = value;
              insuranceInfo.place = formatPlace(
                { ...insuranceInfo, state: insuranceInfo.contactState } as AddressDTO,
              ).place;
              if (isSelfInsurance) {
                setErrors(index, { ...initialErrors });
              } else {
                setErrors(index, { [fieldName]: false });
              }
            }
            return insuranceInfo;
          }
        }
        return insuranceInfo;
      });
      return updatedInsurance;
    });
  };

  const handleAddInsurance = () => {
    setListOfInsurance(
      (currentList) => [
        ...currentList,
        { ...initialInsuranceForm },
      ],
    );
    _setErrors((currentErrors) => [...currentErrors, { ...initialErrors }]);
  };

  const addButtonClass = clsx('text-left !text-populate-green', {
    addButton: listOfInsurance.length <= 3,
    addButtonDisable: listOfInsurance.length === 4,
  });

  const onChangeCashPay = (checked: boolean) => {
    setIsCashPay(checked);
    errors.forEach((error, index) => {
      setErrors(index, { ...initialErrors });
    });
  };

  const handleAddressSuggestion = (
    index: number,
    address: AddressDTO,
  ) => {
    const updatedItem = {
      ...listOfInsurance[index],
      ...address,
      ...formatPlace(address),
      zip: address?.shortZipCode ?? '',
      contactState: address.state,
    };
    setListOfInsurance([
      ...listOfInsurance.slice(0, index),
      updatedItem,
      ...listOfInsurance.slice(index + 1),
    ] as InsuranceFormType[]);
  };

  return (
    <>
      <MetaTag title={titleText} />
      {isUploadingCards && <InsuranceSavingOverlay />}
      <div className="pb-1 relative" aria-busy={isUploadingCards}>
        {(isLoading || isRefreshingInsurance) && (
          <div className="absolute bg-white left-0 right-0 z-10 h-[-webkit-fill-available]">
            <Spinner name="FadeLoader" className="pt-20" />
          </div>
        )}
        {!isEmptyString(errorMessage) && (
          <p className="error-message">{errorMessage}</p>
        )}
        {!isEmptyString(uploadErrorMessage) && (
          <p className="error-message">{uploadErrorMessage}</p>
        )}
        {!isEmptyString(refreshErrorMessage) && (
          <p className="error-message">{refreshErrorMessage}</p>
        )}
        <div className="flex flex-col justify-normal">
          <h2 className={`marginTopZero ${styles.titleText}`}>{titleText}</h2>
          <FormControlLabel
            classes={{ label: styles.inputTextLabel }}
            control={(
              <Checkbox
                checked={isCashPay}
                onChange={(e, checked) => {
                  onChangeCashPay(checked);
                }}
              />
              )}
            label="Out-of-pocket"
          />
          {listOfInsurance?.map((item, index) => {
            const key = index;
            const errorItem = errors[key];
            const { isEmployerInsurance, isSubscriberPatient } = relationshipTypesByName[item?.insuranceOwnerRelationshipType ?? ''] ?? {};
            const isEmpty = isEmptyString(item?.insuranceOwnerRelationshipType);
            const isSelf = isSubscriberPatient || isEmployerInsurance;

            const deactivateOn = !isNil(item.lastInactiveDate)
              ? `Deactivate on ${formatDate(item.lastInactiveDate)}` : undefined;
            const status = item.inactive ? 'Inactive' : 'Active';
            const hasInsuranceId = !isNil(item.insuranceId);

            const selectedState = US_STATES.find(
              (region) => region.name === item.contactState?.trim()
                    || region.abbreviation === item.contactState?.trim(),
            ) ?? null;

            return (
              <Stack key={`insurance-item-${key}`} data-insurance-index={index}>
                <div className="flex justify-end cursor-pointer">
                  <InsuranceAction
                    hasInsuranceId={hasInsuranceId}
                    onClick={() => { removeInsurance(index); }}
                  />
                </div>
                <Label>
                  Relationship to Subscriber
                </Label>
                <select
                  data-insurance-field="insuranceOwnerRelationshipType"
                  value={
                    item?.insuranceOwnerRelationshipType !== ''
                      ? item?.insuranceOwnerRelationshipType
                      : undefined
                  }
                  disabled={hasInsuranceId}
                  onChange={(e) => onChangeInputValue(
                    index,
                    InputTextName.insuranceOwnerRelationshipType,
                    e.target.value,
                  )}
                  style={{ width: '100%' }}
                  className={clsx({
                    [styles.selectError]: errorItem?.insuranceOwnerRelationshipType,
                    [styles.select]: !errorItem?.insuranceOwnerRelationshipType,
                  })}
                >
                  {['Select', ...relationshipTypes].map((name) => (
                    <option
                      key={name}
                      value={name}
                      disabled={name === 'Select'}
                      selected={
                        !item?.insuranceOwnerRelationshipType
                          ? name === 'Select'
                          : item?.insuranceOwnerRelationshipType === name
                      }
                    >
                      {name}
                    </option>
                  ))}
                </select>
                <p className={styles.separator} />
                <div className="flex justify-between items-center">
                  <Label>Insurance Name</Label>
                  <StatusTag
                    text={status}
                    type={item?.inactive ? 'error' : 'success'}
                    title={deactivateOn}
                    hide={!item.inactive}
                  />
                </div>
                <div data-insurance-field="insuranceName">
                  <InsuranceLookup
                    id="insurance-name"
                    readonly={hasInsuranceId}
                    key={item.insuranceName}
                    placeholder="Enter Insurance Name"
                    onChange={(val) => {
                      onChangeInputValue(
                        index,
                        InputTextName.insuranceName,
                        val,
                      );
                    }}
                    error={errorItem?.insuranceName}
                    value={item?.insuranceName}
                    showFavorites
                  />
                </div>
                {!hasInsuranceId && (
                  <div className="flex items-center -mt-2">
                    <p className="text-sm">Can&apos;t find your exact insurance?</p>
                    <InsuranceAction
                      hasInsuranceId={hasInsuranceId}
                      onClick={() => {
                        clickNextOrSubmit(nextPage, index);
                      }}
                      text="Skip"
                      hide
                      className="text-primary font-semibold"
                    />
                  </div>
                )}
                <p className={styles.separator} />
                <Label>Member Number</Label>
                <div data-insurance-field="memberNumber">
                  <InputText
                    onChange={(e) => onChangeInputValue(
                      index,
                      InputTextName.memberNumber,
                      e.target.value?.replace(/[^A-Za-z0-9]/g, ''),
                    )}
                    name="memberNumber"
                    value={item?.memberNumber}
                    error={errorItem?.memberNumber}
                    disabled={hasInsuranceId}
                  />
                </div>
                {
                  isEmployerInsurance
                    && (
                    <>
                      <p className={styles.separator} />
                      <Label>
                        Employer Name
                      </Label>
                      <div data-insurance-field="employerName">
                        <InputText
                          onChange={(e) => onChangeInputValue(
                            index,
                            InputTextName.employerName,
                            e.target.value.trimStart(),
                          )}
                          disabled={hasInsuranceId}
                          name="employerName"
                          value={item?.employerName}
                          error={errorItem?.employerName}
                        />
                      </div>
                    </>
                    )
                  }
                {(!isEmpty && !isSelf) && (
                <>
                  <p className={styles.separator} />
                  <Label>
                    Subscriber First Name
                  </Label>
                  <div data-insurance-field="firstName">
                    <InputText
                      onChange={(e) => onChangeInputValue(
                        index,
                        InputTextName.firstName,
                        e.target.value.trimStart(),
                      )}
                      disabled={hasInsuranceId}
                      name="firstName"
                      value={item?.firstName}
                      error={errorItem?.firstName}
                    />
                  </div>
                  <p className={styles.separator} />
                  <Label>
                    Subscriber Middle Name
                  </Label>
                  <InputText
                    onChange={(e) => onChangeInputValue(
                      index,
                      InputTextName.middleName,
                      e.target.value.trimStart(),
                    )}
                    disabled={hasInsuranceId}
                    name="middleName"
                    value={item?.middleName}
                    error={errorItem?.middleName}
                  />
                  <p className={styles.separator} />
                  <Label>
                    Subscriber Last Name
                  </Label>
                  <div data-insurance-field="lastName">
                    <InputText
                      onChange={(e) => onChangeInputValue(
                        index,
                        InputTextName.lastName,
                        e.target.value.trimStart(),
                      )}
                      disabled={hasInsuranceId}
                      name="lastName"
                      value={item?.lastName}
                      error={errorItem?.lastName}
                    />
                  </div>
                  <p className={styles.separator} />
                  <Label>
                    Subscriber Gender
                  </Label>
                  <select
                    data-insurance-field="gender"
                    value={item?.gender !== '' ? item?.gender : undefined}
                    onChange={(e) => onChangeInputValue(
                      index,
                      InputTextName.gender,
                      e.target.value.trimStart(),
                    )}
                    disabled={hasInsuranceId}
                    style={{ width: '100%' }}
                    className={errorItem?.gender ? styles.selectError : styles.select}
                  >
                    {patientSex.map((name) => (
                      <option
                        key={name}
                        disabled={name === 'Select'}
                        selected={name === 'Select'}
                        defaultValue={name === 'Select' ? 'Select' : ''}
                        value={name}
                      >
                        {name}
                      </option>
                    ))}
                  </select>
                  <p className={styles.separator} />
                  <Label>
                    Subscriber Date of Birth
                  </Label>
                  <div data-insurance-field="dob">
                    <MuiDatePicker
                      disabled={hasInsuranceId}
                      onChange={(value) => onChangeInputValue(
                        index,
                        InputTextName.dob,
                        value?.toString() ?? '',
                      )}
                      value={!isEmptyString(item?.dob) ? dayjs(item?.dob) : undefined}
                      error={errorItem?.dob}
                      disableFuture
                    />
                  </div>
                  <p className={styles.separator} />
                  <Label>
                    Subscriber Phone Number
                  </Label>
                  <div data-insurance-field="phoneNumber">
                    <PhoneInput
                      disabled={hasInsuranceId}
                      error={errorItem?.phoneNumber}
                      onChange={(value) => onChangeInputValue(
                        index,
                        InputTextName.phoneNumber,
                        value as string,
                      )}
                      value={item?.phoneNumber}
                    />
                  </div>
                  <p className={styles.separator} />
                  <Label>Subscriber Address</Label>
                  <Label>Address</Label>
                  <AddressSuggestion
                    value={item.place}
                    disabled={hasInsuranceId}
                    onChange={(value) => handleAddressSuggestion(index, value)}
                  />
                  <Label>Street</Label>
                  <InputText
                    disabled={hasInsuranceId}
                    onChange={(e) => onChangeInputValue(
                      index,
                      InputTextName.street,
                      e.target.value.trimStart(),
                    )}
                    name="street"
                    value={item?.street}
                    error={errorItem?.street}
                  />

                  <p className={styles.separator} />

                  <Label>Unit</Label>
                  <InputText
                    disabled={hasInsuranceId}
                    onChange={(e) => onChangeInputValue(
                      index,
                      InputTextName.unit,
                      e.target.value.trimStart(),
                    )}
                    name="unit"
                    value={item?.unit}
                    error={errorItem?.unit}
                  />

                  <p className={styles.separator} />

                  <Label>City</Label>
                  <InputText
                    disabled={hasInsuranceId}
                    onChange={(e) => onChangeInputValue(
                      index,
                      InputTextName.city,
                      e.target.value.trimStart(),
                    )}
                    name="city"
                    value={item?.city}
                    error={errorItem?.city}
                  />

                  <p className={styles.separator} />

                  <Label>State</Label>
                  <Autocomplete<USStateType>
                    disabled={hasInsuranceId}
                    options={US_STATES}
                    onChange={(e, value) => onChangeInputValue(
                      index,
                      InputTextName.contactState,
                      value?.abbreviation ?? '',
                    )}
                    value={selectedState}
                    getOptionLabel={(option) => `${option.name} - ${option.abbreviation}`}
                    renderInput={(params) => <TextField {...params} />}
                    id="state"
                    classes={{
                      root: clsx('!w-full rounded', { 'has-error': errorItem?.contactState }),
                    }}
                  />

                  <p className={styles.separator} />

                  <Wrapper
                    error={errorItem?.zip}
                    id="zip-code "
                    label="Zip"
                    showErrorText={false}
                  >
                    <ZipCodeTextField
                      disabled={hasInsuranceId}
                      onChange={(value) => onChangeInputValue(
                        index,
                        InputTextName.zip,
                        value as string,
                      )}
                      value={item?.zip}
                    />
                  </Wrapper>
                  <p className={styles.separator} />
                </>
                )}

                {insuranceSlotValues.length > 0 && (
                  <>
                    <Label>Billing Order</Label>
                    <select
                      data-insurance-field="insuranceSlot"
                      value={item.insuranceSlot ?? ''}
                      disabled={hasInsuranceId}
                      onChange={(e) => {
                        const val = e.target.value as InsuranceSlot | '';
                        handleSlotChange(val === '' ? undefined : val, index);
                      }}
                      style={{ width: '100%' }}
                      className={clsx({
                        [styles.selectError]: errorItem?.insuranceSlot,
                        [styles.select]: !errorItem?.insuranceSlot,
                      })}
                    >
                      <option value="" disabled>Select Billing Order</option>
                      {insuranceSlotValues.map((slot) => (
                        <option key={slot} value={slot}>
                          {getInsuranceSlotLabel(slot)}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <p className={styles.separator} />

                {hasInsuranceId && item.insuranceId ? (
                  <>
                    <Label>Insurance Cards</Label>
                    <InsuranceAttachmentCards
                      insuranceId={item.insuranceId}
                      patientId={patient?.patientId}
                    />
                  </>
                ) : (
                  <>
                    <Label>
                      Please, upload a picture of the
                      {' '}
                      <strong>front</strong>
                      {' '}
                      of your insurance card
                    </Label>
                    <UploaderElement
                      getImage={(value) => onChangeInputValue(
                        index,
                        InputTextName.insuranceFrontSide,
                        value,
                      )}
                      imageBase64={item?.insuranceFrontSide}
                      acceptExtensions={getAllowedFileTypesByCategory()}
                    />
                    <p className={styles.separator} />

                    <Label>
                      Please, upload a picture of the
                      {' '}
                      <strong>back</strong>
                      {' '}
                      of your insurance card
                    </Label>
                    <UploaderElement
                      getImage={(value) => onChangeInputValue(
                        index,
                        InputTextName.insuranceBackSide,
                        value,
                      )}
                      imageBase64={item?.insuranceBackSide}
                      acceptExtensions={getAllowedFileTypesByCategory()}
                    />
                  </>
                )}
                {listOfInsurance.length !== index && (
                  <p
                    className={`${styles.separator} border-slate-200 border !mt-4`}
                  />
                )}
              </Stack>
            );
          })}

          <button
            className={addButtonClass}
            onClick={handleAddInsurance}
            disabled={listOfInsurance.length === 4}
            type="button"
          >
            <AddIcon fontSize="small" />
            <span>Add another insurance</span>
          </button>
        </div>
        {patient && (
          <UnassociatedInsuranceCards
            patientId={patient.patientId}
          />
        )}
        {!isLoading && !isRefreshingInsurance && (
          <div className="next-button-fixed">
            <Button
              onClick={() => clickNextOrSubmit(nextPage)}
              disabled={isUploadingCards}
            >
              {isUploadingCards ? 'Saving insurance...' : 'Next'}
            </Button>
          </div>
        )}
      </div>

      <Dialog
        open={slotConflictDialogOpen}
        title="Billing Order Conflict"
        onClose={handleCloseSlotConflictDialog}
        disableBackdropClick
      >
        <div className="flex flex-col gap-4">
          <p>
            {conflictingInsuranceName
              ? (
                <>
                  <strong>{conflictingInsuranceName}</strong>
                  {' is currently set as '}
                  <strong>{conflictingSlotLabel}</strong>
                  .
                </>
              )
              : (
                <>
                  {'An active insurance is already set as '}
                  <strong>{conflictingSlotLabel}</strong>
                  .
                </>
              )}
          </p>
          <p className="text-gray-700">
            Multiple insurances cannot share the same billing order.
            Please ensure each insurance is assigned a unique billing priority.
          </p>
          <div className="flex justify-end">
            <Button onClick={handleCloseSlotConflictDialog}>
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default InsuranceIdScreen;
